'use client';

import { Skeleton } from '@mui/material';
import { useMemo, useState } from 'react';

import {
  useGetJobDeliverablesQuery,
  useGetJobQuery,
} from '@/features/jobs/api/jobsApi';

import { DeliverableCard } from './DeliverableCard';
import styles from './JobTasksComponents.module.scss';

import type { JobDeliverable } from '@/features/jobs/types';
import type { JobKind } from '@/features/jobs/types';

type JobDetailsTasksViewProps = {
  jobId: string;
  canEdit: boolean;
  jobKind: JobKind;
  serviceTypeIds: string[];
  retainerCycleId?: string | null;
};

export function JobDetailsTasksView({
  jobId,
  canEdit,
  jobKind,
  serviceTypeIds,
  retainerCycleId = null,
}: JobDetailsTasksViewProps) {
  const [localPatches, setLocalPatches] = useState<
    Record<string, Partial<JobDeliverable>>
  >({});
  const isRetainer = jobKind === 'retainer';

  const { data, isLoading, isFetching } = useGetJobDeliverablesQuery(
    { jobId, params: { page_size: 100 } },
    { skip: !jobId || isRetainer, refetchOnMountOrArgChange: true },
  );
  const {
    data: jobResponse,
    isLoading: isJobLoading,
    isFetching: isJobFetching,
  } = useGetJobQuery(
    {
      jobId,
      retainerCycleId,
    },
    {
      skip: !jobId || !isRetainer,
    },
  );

  const rows = useMemo(() => {
    const retainerDeliverables = isRetainer
      ? jobResponse?.data?.deliverables
      : null;
    const payload = (retainerDeliverables ?? data?.data) as
      | { results?: JobDeliverable[] }
      | JobDeliverable[]
      | null;
    if (!payload) {
      return [];
    }

    const source = Array.isArray(payload) ? payload : (payload.results ?? []);
    return source
      .map((row) => ({ ...row, ...(localPatches[row.id] ?? {}) }))
      .sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999));
  }, [data?.data, isRetainer, jobResponse?.data?.deliverables, localPatches]);
  const isWaitingForScopedDeliverables =
    isRetainer &&
    !jobResponse?.data?.deliverables &&
    (isJobLoading || isJobFetching);

  if (
    isLoading ||
    (isFetching && rows.length === 0) ||
    isWaitingForScopedDeliverables
  ) {
    return (
      <div className={styles.tasksPageLoading}>
        <Skeleton variant="rectangular" height={120} />
        <Skeleton variant="rectangular" height={280} />
      </div>
    );
  }

  return (
    <section className={styles.tasksPage}>
      {rows.map((deliverable) => (
        <DeliverableCard
          key={deliverable.id}
          jobId={jobId}
          jobKind={jobKind}
          deliverable={deliverable}
          serviceTypeIds={serviceTypeIds}
          retainerCycleId={retainerCycleId}
          readOnly={!canEdit}
          onPatched={(patch) => {
            setLocalPatches((current) => ({
              ...current,
              [deliverable.id]: {
                ...(current[deliverable.id] ?? {}),
                ...patch,
              },
            }));
          }}
        />
      ))}
    </section>
  );
}
